import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { RoomShell } from "@/components/RoomShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

function CaseModal({ caseData, onClose, onSolved, solved, isAuthed }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (selected === null) return;
    setLoading(true);
    try {
      const { data } = await api.post("/cases/solve", { case_id: caseData.id, answer_index: selected });
      setResult(data);
      if (data.correct) { onSolved(); toast.success("Badge earned."); }
      else toast.error("Wrong lead.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,7,5,0.85)", backdropFilter: "blur(6px)" }} data-testid="case-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="paper-card w-full max-w-lg rounded-md p-8 max-h-[88vh] overflow-y-auto">
        <p className="font-typewriter text-[11px] uppercase tracking-[0.3em]" style={{ color: "#8B5A2B" }}>{caseData.category} · {caseData.id}</p>
        <h2 className="font-serif-hq text-3xl mt-1 mb-4" style={{ color: "#2A2015" }}>{caseData.title}</h2>
        <p className="text-sm leading-relaxed mb-4" style={{ color: "#4A3B28" }}>{caseData.content}</p>
        <p className="font-typewriter text-sm mb-3" style={{ color: "#2A2015" }}>{caseData.question}</p>

        <div className="space-y-2">
          {caseData.options.map((opt, i) => (
            <button key={i} data-testid={`case-option-${i}`} disabled={!!result}
              onClick={() => setSelected(i)}
              className="w-full text-left rounded px-4 py-3 text-sm transition-colors"
              style={{
                background: selected === i ? "#2A2015" : "transparent",
                color: selected === i ? "#FDF6E3" : "#2A2015",
                border: "1px solid #2A2015",
              }}>
              {opt}
            </button>
          ))}
        </div>

        {result && (
          <p className="mt-4 text-sm font-typewriter" style={{ color: result.correct ? "#166534" : "#991B1B" }}>{result.message}</p>
        )}

        <div className="flex gap-3 mt-6">
          {!result && !solved && (
            <button data-testid="case-submit-btn" onClick={submit} disabled={loading || selected === null}
              className="flex-1 rounded py-2.5 font-typewriter text-xs uppercase tracking-widest disabled:opacity-50"
              style={{ background: "#991B1B", color: "#FDF6E3" }}>
              {loading ? "Filing…" : "Submit Verdict"}
            </button>
          )}
          <button data-testid="case-close-btn" onClick={onClose}
            className="flex-1 rounded py-2.5 font-typewriter text-xs uppercase tracking-widest"
            style={{ border: "1px solid #2A2015", color: "#2A2015" }}>Close file</button>
        </div>
        {!result && !solved && !isAuthed && (
          <p className="text-xs mt-3 text-center" style={{ color: "#6b5f4d" }}>
            No account needed to investigate.{" "}
            <button onClick={() => navigate("/enter")} className="underline" style={{ color: "#8B5A2B" }}>Sign in</button> to keep your badges.
          </p>
        )}
      </motion.div>
    </div>
  );
}

export default function EvidenceRoom() {
  const { user, refresh } = useAuth();
  const [cases, setCases] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)); }, []);

  const solvedIds = user?.solved_cases || [];

  return (
    <RoomShell testid="evidence-room" kicker="Evidence Room" title="Case Files">
      <p className="text-[#A89A80] max-w-2xl -mt-6 mb-10 leading-relaxed">
        Every file is an investigation into how media shapes the mind. Read the evidence, name the hidden mechanism,
        and close the case to earn a badge.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5">
        {cases.map((c, idx) => {
          const solved = solvedIds.includes(c.id);
          return (
            <motion.button
              key={c.id}
              data-testid={`case-card-${c.id}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.07 }}
              onClick={() => setActive(c)}
              className="text-left rounded-md p-6 transition-colors hover:border-[#F59E0B]/50 relative"
              style={{ background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" }}
            >
              {solved && (
                <span className="absolute top-4 right-4 flex items-center gap-1 font-typewriter text-[10px] uppercase tracking-widest text-[#4ade80]">
                  <CheckCircle2 size={13} /> Closed
                </span>
              )}
              <p className="font-typewriter text-[10px] uppercase tracking-[0.3em] text-[#D97706]">{c.category}</p>
              <h3 className="font-serif-hq text-2xl text-[#FDF6E3] mt-2 mb-2">{c.title}</h3>
              <p className="text-sm text-[#A89A80] leading-relaxed">{c.summary}</p>
            </motion.button>
          );
        })}
      </div>

      {active && (
        <CaseModal
          caseData={active}
          solved={solvedIds.includes(active.id)}
          isAuthed={!!user}
          onClose={() => setActive(null)}
          onSolved={refresh}
        />
      )}
    </RoomShell>
  );
}
